'use client'

// Animation
import { motion } from 'framer-motion'
import {
  Container,
  fadeInUp,
  Section,
  Spotlight,
  staggerContainer,
} from 'design-studio'

// Layout
import Image from 'next/image'
import Link from 'next/link'
// UI components
import { SectionHeader } from '@/components/ui/section-header'

const APPLICATIONS = [
  {
    number: '01',
    title: 'Formal Verification',
    subtitle: 'Prove code correct — or find exactly where it breaks',
    description:
      'Mathematically verify algorithm properties with push-button automation. ImandraX handles most proofs fully automatically using its Boyer-Moore-style inductive waterfall. When conjectures are false, it synthesizes concrete counterexamples — including higher-order ones that produce functions as witnesses.',
    links: [
      {
        label: 'Autonomous vehicle controller',
        href: '/docs/case-studies/autonomous-vehicle/',
      },
      {
        label: 'Ripple-carry adder tutorial',
        href: '/docs/tutorials/ripple-carry-adder/',
      },
      {
        label: 'Counterexample synthesis',
        href: '/docs/verification/counterexamples/',
      },
    ],
    image: {
      src: '/images/core/applications-formal-verification.webp',
      width: 766,
      height: 700,
      alt: 'Formal Verification',
    },
    imageRight: true,
    accent: 'from-teal-600/5',
  },
  {
    number: '02',
    title: 'Symbolic Reasoning',
    subtitle: 'Map every possible behavior of your system',
    description:
      'Decompose the input space of any function into exhaustive, disjoint regions where behavior is invariant — a complete map of all possible outcomes. Solve complex constraints using executable programs as logic, and optimize by searching for solutions that satisfy constraints while minimizing or maximizing a cost function.',
    links: [
      {
        label: 'Region decomposition docs',
        href: '/docs/region-decomposition/',
      },
      {
        label: 'Decomposition applications',
        href: '/docs/region-decomposition/applications/',
      },
    ],
    image: {
      src: '/images/core/core-4-symbolic-reasoning.webp',
      width: 1036,
      height: 712,
      alt: 'Symbolic Reasoning',
    },
    imageRight: false,
    accent: 'from-cyan-600/5',
  },
  {
    number: '03',
    title: 'Rule Synthesis',
    subtitle: 'Extract auditable logic from complex data and systems',
    description:
      'Extract logical patterns from complex, structured and sequential data and synthesize them into a single executable, auditable model. Convert the model into various target languages or prose, detect anomalies in out-of-sample data, and generate corrective actions.',
    links: [
      {
        label: 'Simplification & rewrite rules',
        href: '/docs/verification/simplification/',
      },
      {
        label: 'IML language guide',
        href: '/docs/language/',
      },
      {
        label: 'Key concepts',
        href: '/docs/getting-started/key-concepts/',
      },
    ],
    image: {
      src: '/images/core/core-5-rule-synthesis--diagram.webp',
      width: 1039,
      height: 682,
      alt: 'Rule Synthesis',
    },
    imageRight: true,
    accent: 'from-blue-600/5',
  },
]

export function ApplicationsVisual() {
  return (
    <Section
      id="applications-detail"
      className="relative scroll-mt-32 overflow-hidden bg-zinc-200 py-8 backdrop-blur-md sm:py-16 dark:bg-slate-800/10"
    >
      <Container className="relative z-10 max-w-7xl">
        <SectionHeader
          pretitle="Applications"
          title="Core Applications"
          description="ImandraX spans formal verification, symbolic reasoning and rule synthesis — all accessible to engineers without requiring formal methods expertise."
          pretitleColor="text-cyan-500/80"
          className="relative z-10 mb-20"
        />

        <div className="mx-auto max-w-6xl space-y-22">
          {APPLICATIONS.map((app, index) => (
            <motion.div
              key={app.number}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerContainer}
              className={`grid grid-cols-1 items-start gap-12 lg:grid-cols-2 lg:gap-16 ${
                !app.imageRight ? 'lg:[&>*:first-child]:order-last' : ''
              }`}
            >
              {/* Text */}
              <motion.div
                variants={fadeInUp}
                className="relative z-10 space-y-5"
              >
                {/* <div className="flex items-center gap-3">
                  <span className="font-mono text-xs font-semibold tracking-widest text-cyan-500/80 uppercase">
                    {app.number}
                  </span>
                  <div className="h-px flex-1 bg-slate-700/40" />
                </div> */}
                <div>
                  <h3 className="font-display text-3xl font-light tracking-tight text-white">
                    {app.title}
                  </h3>
                  <p className="mt-1 font-mono text-sm text-cyan-400/80">
                    {app.subtitle}
                  </p>
                </div>
                <p className="font-display text-base leading-relaxed font-light text-slate-300">
                  {app.description}
                </p>
                {app.links.length > 0 && (
                  <ul className="space-y-1.5 pt-2">
                    {app.links.map((link) => (
                      <li key={link.href}>
                        <Link
                          href={link.href}
                          className="inline-flex items-center gap-1.5 font-mono text-sm text-blue-400 underline-offset-2 transition-colors hover:text-blue-300 hover:underline"
                        >
                          {link.label}
                          <svg
                            viewBox="0 0 12 12"
                            className="h-3 w-3"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            aria-hidden="true"
                          >
                            <path
                              d="M1 6h10M7 2l4 4-4 4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>

              {/* Image */}
              <motion.div
                variants={fadeInUp}
                className="flex items-center justify-center"
              >
                <div
                  className={`relative w-full overflow-hidden rounded-2xl border border-white/10 bg-slate-900/40 p-3 backdrop-blur-sm md:p-6`}
                >
                  <Image
                    src={app.image.src}
                    width={app.image.width}
                    height={app.image.height}
                    alt={app.image.alt}
                    quality={90}
                    className="w-full object-contain"
                  />
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        <Spotlight
          color="from-teal-600/8"
          className="top-200 -left-200 z-0 size-512"
        />
        <Spotlight
          color="from-cyan-600/8"
          className="-right-200 bottom-200 z-0 size-512"
        />
      </Container>
    </Section>
  )
}
